import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, BrainCircuit, ArrowRight, LogIn, ChevronDown, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { LINKS } from "../mock";

const aboutItems = [
  { to: "/about", label: "Mission & Vision" },
  { to: "/research", label: "Research" },
  { to: "/blog", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

const links = [
  { to: "/assessments", label: "Assessments" },
  { to: "/partner", label: "Business" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setAboutOpen(false);
  }, [location.pathname]);

  const aboutActive = aboutItems.some((a) => location.pathname === a.to);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl shadow-[0_8px_30px_-18px_rgba(79,70,229,0.35)] border-b border-violet-100" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 sm:h-[72px] items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            <span className="grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-xl cta-gradient text-white shadow-lg shadow-violet-300/50">
              <BrainCircuit className="h-5 w-5 sm:h-6 sm:w-6" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-[18px] font-extrabold tracking-tight text-slate-900">Map<span className="brand-gradient-text">MyKid</span></span>
              <span className="text-[11px] font-medium text-slate-400 hidden sm:block">Assess &amp; Discover True Potential</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-0.5">
            <Link
              to="/"
              className={`relative px-3 py-2 rounded-full text-[14px] font-semibold transition-colors ${location.pathname === "/" ? "text-violet-700" : "text-slate-600 hover:text-violet-700"}`}
              data-testid="nav-home"
            >
              Home
              {location.pathname === "/" && <span className="absolute inset-x-2 -bottom-0.5 h-0.5 rounded-full cta-gradient" />}
            </Link>
            <Link
              to="/assessments"
              className={`relative px-3 py-2 rounded-full text-[14px] font-semibold transition-colors ${location.pathname === "/assessments" ? "text-violet-700" : "text-slate-600 hover:text-violet-700"}`}
              data-testid="nav-assessments"
            >
              Assessments
              {location.pathname === "/assessments" && <span className="absolute inset-x-2 -bottom-0.5 h-0.5 rounded-full cta-gradient" />}
            </Link>

            {/* About Us dropdown */}
            <div className="relative" onMouseEnter={() => setAboutOpen(true)} onMouseLeave={() => setAboutOpen(false)}>
              <button
                className={`relative flex items-center gap-1 px-3 py-2 rounded-full text-[14px] font-semibold transition-colors ${aboutActive ? "text-violet-700" : "text-slate-600 hover:text-violet-700"}`}
                data-testid="nav-about-trigger"
              >
                About Us <ChevronDown className={`h-4 w-4 transition-transform ${aboutOpen ? "rotate-180" : ""}`} />
                {aboutActive && <span className="absolute inset-x-2 -bottom-0.5 h-0.5 rounded-full cta-gradient" />}
              </button>
              <div className={`absolute left-0 top-full pt-3 w-56 transition-all duration-200 ${aboutOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1"}`}>
                <div className="rounded-2xl border border-violet-100 bg-white shadow-xl shadow-violet-200/40 p-2">
                  {aboutItems.map((a) => (
                    <Link
                      key={a.to}
                      to={a.to}
                      className={`block rounded-xl px-4 py-3 text-[16px] font-semibold transition-colors ${location.pathname === a.to ? "bg-violet-50 text-violet-700" : "text-slate-700 hover:bg-violet-50 hover:text-violet-700"}`}
                      data-testid={`nav-about-${a.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      {a.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {links.slice(1).map((l) => {
              const active = location.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`relative px-3 py-2 rounded-full text-[14px] font-semibold transition-colors ${active ? "text-violet-700" : "text-slate-600 hover:text-violet-700"}`}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                  {active && <span className="absolute inset-x-2 -bottom-0.5 h-0.5 rounded-full cta-gradient" />}
                </Link>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center gap-1.5">
            <Link to="/discover" data-testid="nav-discover" className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 px-3.5 h-10 text-[14px] font-bold text-white shadow-lg shadow-teal-500/30 hover:opacity-95 transition-opacity">
              <Sparkles className="h-4 w-4" /> Free: Discover
            </Link>
            <a href={LINKS.login} target="_blank" rel="noopener noreferrer" data-testid="nav-register-login">
              <Button className="cta-gradient hover:opacity-95 text-white rounded-full px-4 h-10 text-[14px] font-semibold shadow-lg shadow-violet-300/40">
                <LogIn className="mr-1.5 h-4 w-4" /> Register / Login
              </Button>
            </a>
            <Button
              onClick={() => navigate("/book")}
              variant="outline"
              className="rounded-full px-4 h-10 text-[14px] font-semibold border-violet-200 text-violet-700 hover:bg-violet-50"
              data-testid="nav-book"
            >
              Book Assessment <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid h-11 w-11 place-items-center rounded-xl border border-violet-100 bg-white/80 text-slate-700"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-violet-100 animate-fade-up max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link to="/discover" className="flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-lg shadow-teal-500/30 mb-2" data-testid="nav-m-discover">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/20"><Sparkles className="h-5 w-5" /></span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2 text-[16px] font-extrabold leading-tight">Discover <span className="rounded-full bg-white/25 px-2 py-0.5 text-[11px] font-bold">FREE</span></span>
                <span className="block text-[12px] text-white/90 leading-tight">A free taster — different from the paid assessments</span>
              </span>
              <ArrowRight className="h-5 w-5 shrink-0" />
            </Link>
            <Link to="/" className={`block px-4 py-3 rounded-xl text-[17px] font-semibold ${location.pathname === "/" ? "bg-violet-50 text-violet-700" : "text-slate-700 hover:bg-slate-50"}`} data-testid="nav-m-home">Home</Link>
            <Link to="/assessments" className={`block px-4 py-3 rounded-xl text-[17px] font-semibold ${location.pathname === "/assessments" ? "bg-violet-50 text-violet-700" : "text-slate-700 hover:bg-slate-50"}`} data-testid="nav-m-assessments">Assessments</Link>

            <div className="px-4 pt-2 pb-1 text-[13px] font-bold uppercase tracking-wide text-slate-400">About Us</div>
            {aboutItems.map((a) => (
              <Link key={a.to} to={a.to} className={`block px-4 py-3 rounded-xl text-[16px] font-semibold ${location.pathname === a.to ? "bg-violet-50 text-violet-700" : "text-slate-700 hover:bg-slate-50"}`} data-testid={`nav-m-about-${a.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {a.label}
              </Link>
            ))}

            {links.slice(1).map((l) => (
              <Link key={l.to} to={l.to} className={`block px-4 py-3 rounded-xl text-[17px] font-semibold ${location.pathname === l.to ? "bg-violet-50 text-violet-700" : "text-slate-700 hover:bg-slate-50"}`} data-testid={`nav-m-${l.label.toLowerCase()}`}>
                {l.label}
              </Link>
            ))}

            <a href={LINKS.login} target="_blank" rel="noopener noreferrer" className="block">
              <Button className="w-full cta-gradient text-white rounded-xl h-12 text-[16px] font-semibold mt-2">
                <LogIn className="mr-1.5 h-4 w-4" /> Register / Login
              </Button>
            </a>
            <Button
              onClick={() => navigate("/book")}
              variant="outline"
              className="w-full rounded-xl h-12 text-[16px] font-semibold border-violet-200 text-violet-700 hover:bg-violet-50 mt-2"
            >
              Book Assessment <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
